/**
 * PaymentDetailPage - View payment details with applications
 * 
 * Features:
 * - Payment info (amount, method, date, reference)
 * - Applied bills/invoices list
 * - Void payment action
 * - Navigation to vendor and applied documents
 */

import React, { useState, useEffect, useCallback } from 'react';
import { paymentsAPI, checksAPI, bankAccountsAPI } from '../../utils/api';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DollarSign,
  Building2,
  ArrowLeft,
  Loader2,
  Calendar,
  CreditCard,
  FileText,
  Ban,
  CheckCircle,
  Clock,
  Hash,
  Eye,
  RefreshCw,
  Printer,
} from 'lucide-react';

// Payment status configuration
const PAYMENT_STATUS_CONFIG = {
  posted: { label: 'Posted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  void: { label: 'Void', color: 'bg-gray-100 text-gray-500', icon: Ban },
};

// Payment method labels
const PAYMENT_METHOD_LABELS = {
  check: 'Check',
  ach: 'ACH / Bank Transfer',
  credit_card: 'Credit Card',
  wire: 'Wire Transfer',
  cash: 'Cash',
  other: 'Other',
};

// Format currency (from cents)
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const PaymentDetailPage = ({ 
  paymentId, 
  onBack,
  onNavigateToVendor,
  onNavigateToBill,
  onNavigateToInvoice,
}) => {
  const [payment, setPayment] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [applications, setApplications] = useState([]);
  const [appliedTotal, setAppliedTotal] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Write Check Modal state
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [checkPreview, setCheckPreview] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [checkDate, setCheckDate] = useState(new Date().toISOString().split('T')[0]);
  const [checkMemo, setCheckMemo] = useState('');
  const [creatingCheck, setCreatingCheck] = useState(false);

  // Fetch bank accounts on mount
  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const accounts = await bankAccountsAPI.getAccounts({ is_active: true });
        setBankAccounts(accounts || []);
        const defaultAcc = accounts?.find(a => a.is_default);
        if (defaultAcc) setSelectedBankAccount(defaultAcc.id);
      } catch (err) {
        console.error('Failed to fetch bank accounts', err);
      }
    };
    fetchBankAccounts();
  }, []);

  // Fetch payment data
  const fetchPayment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await paymentsAPI.getPayment(paymentId);
      setPayment(data.payment);
      setVendor(data.vendor);
      setCustomer(data.customer);
      setApplications(data.applications || []);
      setAppliedTotal(data.applied_total_cents || 0);
      setRemainingAmount(data.remaining_cents || 0);
    } catch (err) {
      setError(err.message || 'Failed to load payment');
      toast.error(err.message || 'Failed to load payment');
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  useEffect(() => {
    if (paymentId) {
      fetchPayment();
    }
  }, [paymentId, fetchPayment]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchPayment();
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchPayment,
    enabled: !loading && !!paymentId,
    refreshing,
    throttleMs: 1500,
  });

  // Open Write Check modal
  const openWriteCheckModal = async () => {
    try {
      const preview = await checksAPI.previewFromPayment(paymentId);
      setCheckPreview(preview);
      setCheckMemo(preview.memo || '');
      setCheckDate(new Date().toISOString().split('T')[0]);
      if (preview.default_bank_account_id) {
        setSelectedBankAccount(preview.default_bank_account_id);
      }
      setShowCheckModal(true);
    } catch (err) {
      toast.error('Failed to load payment details for check');
      console.error(err);
    }
  };

  // Create check from payment
  const handleCreateCheckFromPayment = async (printNow = false) => {
    if (!selectedBankAccount) {
      toast.error('Please select a bank account');
      return;
    }
    
    setCreatingCheck(true);
    try {
      const newCheck = await checksAPI.createCheck({
        bank_account_id: selectedBankAccount,
        date: checkDate,
        payee_name: checkPreview.payee_name,
        payee_address: checkPreview.payee_address || null,
        amount_cents: checkPreview.amount_cents,
        memo: checkMemo || null,
        payment_id: checkPreview.payment_id,
        vendor_id: checkPreview.vendor_id,
        print_now: printNow
      });
      
      toast.success(`Check #${newCheck.check_number} created`);
      setShowCheckModal(false);
      setCheckPreview(null);
      
      if (printNow) {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/checks/${newCheck.id}/pdf`, '_blank');
      }
      
      fetchPayment();
    } catch (err) {
      toast.error(err.message || 'Failed to create check');
      console.error(err);
    } finally {
      setCreatingCheck(false);
    }
  };

  // Void payment
  const handleVoidPayment = async () => {
    if (!window.confirm('Are you sure you want to void this payment? This will reverse all applications and restore document balances.')) {
      return;
    }

    setVoiding(true);
    try {
      await paymentsAPI.voidPayment(paymentId);
      toast.success('Payment voided successfully');
      fetchPayment();
    } catch (err) {
      toast.error(err.message || 'Failed to void payment');
    } finally {
      setVoiding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Payments
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Payment not found'}
        </div>
      </div>
    );
  }

  const statusConfig = PAYMENT_STATUS_CONFIG[payment.status] || PAYMENT_STATUS_CONFIG.posted;
  const StatusIcon = statusConfig.icon;
  const isVoid = payment.status === 'void';
  const isExpense = payment.payment_type === 'expense';

  return (
    <div className="space-y-6" data-testid="payment-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {payment.payment_number || `Payment ${payment.id.slice(0, 8)}`}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={statusConfig.color}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  <Badge variant="outline">
                    {isExpense ? 'Expense' : 'Sales'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Preview/Print PDF */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => {
              const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/payments/admin/${paymentId}/pdf?preview=true`;
              window.open(previewUrl, '_blank');
            }}
            data-testid="preview-payment-btn"
            title="Preview Receipt"
          >
            <Eye className="w-4 h-4" />
          </Button>
          
          {/* Refresh Button */}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-payment-btn"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          
          {/* Write Check Button - Only for expense payments that are not void */}
          {isExpense && !isVoid && (
            <Button 
              variant="outline"
              onClick={openWriteCheckModal}
              className="text-blue-600 hover:text-blue-700"
              data-testid="write-check-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Write Check
            </Button>
          )}
          
          {!isVoid && (
            <Button 
              variant="outline" 
              onClick={handleVoidPayment}
              disabled={voiding}
              className="text-red-600 hover:text-red-700"
              data-testid="void-payment-btn"
            >
              {voiding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
              Void Payment
            </Button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {isVoid && (
        <div className="bg-gray-100 border border-gray-200 text-gray-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Ban className="w-5 h-5" />
          <span>This payment has been <strong>voided</strong>. All applications have been reversed.</span>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vendor/Customer Card */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-400" />
                {isExpense ? 'Vendor' : 'Customer'}
              </h2>
              {vendor && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onNavigateToVendor?.(vendor.id)}
                  data-testid="view-vendor-btn"
                >
                  View {isExpense ? 'Vendor' : 'Customer'}
                </Button>
              )}
            </div>
            {(vendor || customer) ? (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-gray-500" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{vendor?.name || customer?.name}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-400">No {isExpense ? 'vendor' : 'customer'} assigned</p>
            )}
          </div>

          {/* Applied Documents */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-400" />
              Applied To
            </h2>
            
            {applications.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="applications-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Document #</th>
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-right font-medium">Applied</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {applications.map(app => (
                      <tr 
                        key={app.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          if (app.applies_to_type === 'vendor_bill') {
                            onNavigateToBill?.(app.applies_to_id);
                          } else if (app.applies_to_type === 'invoice') {
                            onNavigateToInvoice?.(app.applies_to_id);
                          }
                        }}
                        data-testid={`application-row-${app.id}`}
                      >
                        <td className="px-3 py-2">
                          <Badge variant="outline">
                            {app.applies_to_type === 'vendor_bill' ? 'Bill' : 'Invoice'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-mono text-sm">
                          {app.document_number || app.applies_to_id?.slice(0, 10)}
                        </td>
                        <td className="px-3 py-2 text-gray-500">
                          {formatDate(app.document_date)}
                        </td>
                        <td className="px-3 py-2 text-right font-medium tabular-nums">
                          {formatCurrency(app.applied_amount_cents)}
                        </td>
                        <td className="px-3 py-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (app.applies_to_type === 'vendor_bill') {
                                onNavigateToBill?.(app.applies_to_id);
                              } else if (app.applies_to_type === 'invoice') {
                                onNavigateToInvoice?.(app.applies_to_id);
                              }
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-medium">
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-right">Total Applied:</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(appliedTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No documents applied</p>
                <p className="text-sm mt-1">This payment has not been applied to any bills or invoices.</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Payment Details */}
        <div className="space-y-6">
          {/* Payment Summary */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Payment Summary</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Amount</span>
                <span className="font-bold text-xl tabular-nums">{formatCurrency(payment.amount_cents)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Applied</span>
                <span className="font-medium tabular-nums text-green-600">{formatCurrency(appliedTotal)}</span>
              </div>
              {remainingAmount > 0 && (
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-500">Unapplied</span>
                  <span className="font-medium tabular-nums text-amber-600">{formatCurrency(remainingAmount)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Payment Details</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Payment Date</p>
                  <p className="font-medium">{formatDate(payment.payment_date)}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CreditCard className="w-4 h-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Method</p>
                  <p className="font-medium">{PAYMENT_METHOD_LABELS[payment.payment_method] || payment.payment_method}</p>
                </div>
              </div>
              
              {payment.payment_reference && (
                <div className="flex items-start gap-3">
                  <Hash className="w-4 h-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Reference / Check #</p>
                    <p className="font-mono text-sm">{payment.payment_reference}</p>
                  </div>
                </div>
              )}
              
              {payment.memo && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500 mb-1">Memo</p>
                  <p className="text-sm bg-gray-50 p-2 rounded">{payment.memo}</p>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Record Info</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span>{formatDate(payment.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">ID</span>
                <span className="font-mono text-xs">{payment.id}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Write Check Modal */}
      <Dialog open={showCheckModal} onOpenChange={setShowCheckModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Write Check from Payment</DialogTitle>
          </DialogHeader>
          
          {checkPreview && (
            <div className="space-y-4 py-4">
              {/* Payment Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Payment #</span>
                  <span className="font-medium">{checkPreview.payment_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Payee</span>
                  <span className="font-medium">{checkPreview.payee_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-semibold text-lg">{formatCurrency(checkPreview.amount_cents)}</span>
                </div>
                <div className="text-xs text-gray-500 italic">
                  {checkPreview.amount_words}
                </div>
              </div>

              {/* Line Items */}
              {checkPreview.line_items?.length > 0 && (
                <div>
                  <Label className="text-sm text-gray-600">Bills/POs Covered</Label>
                  <div className="border rounded-lg overflow-hidden mt-1">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Bill/PO</th>
                          <th className="px-3 py-2 text-right font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {checkPreview.line_items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">
                              {item.bill_number && <span className="text-gray-600">Bill #{item.bill_number}</span>}
                              {item.bill_number && item.po_number && <span className="mx-1">•</span>}
                              {item.po_number && <span className="text-gray-500">PO #{item.po_number}</span>}
                              {!item.bill_number && !item.po_number && item.description}
                            </td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.amount_cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Check Details Form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="check-bank-account">Bank Account *</Label>
                  <Select value={selectedBankAccount} onValueChange={setSelectedBankAccount}>
                    <SelectTrigger id="check-bank-account" data-testid="check-bank-account-select">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.account_name} {acc.is_default && '(Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {bankAccounts.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No bank accounts. Add one in Settings.</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="check-date">Check Date</Label>
                  <Input
                    id="check-date"
                    type="date"
                    value={checkDate}
                    onChange={(e) => setCheckDate(e.target.value)}
                    data-testid="check-date-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="check-memo">Memo</Label>
                <Input
                  id="check-memo"
                  value={checkMemo}
                  onChange={(e) => setCheckMemo(e.target.value)}
                  placeholder="Optional memo"
                  data-testid="check-memo-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCheckModal(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleCreateCheckFromPayment(false)}
              disabled={creatingCheck || !selectedBankAccount}
              data-testid="save-draft-check-btn"
            >
              {creatingCheck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save as Draft
            </Button>
            <Button
              onClick={() => handleCreateCheckFromPayment(true)}
              disabled={creatingCheck || !selectedBankAccount}
              data-testid="create-print-check-btn"
            >
              {creatingCheck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
              Create & Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentDetailPage;
